import { useEffect, useState } from "react";
import { Citation } from "../../helpers/Models";
import TypingIndicator from "../markdown/TypingIndicator";
import "./index.css";
import { useRef } from "react";
import { highlightPlainText } from "../../helpers/highlight";
import { useDocument } from "../../hooks/use-document";
import { useGetCitedExcerpt } from "../../hooks/use-cited-excerpt";
import { Tabs } from "../citation-container";

export interface CitedExcerptsOutput {
  context: string;
  matched_line: string;
  detail?: string;
  excerpt_link?: string;
  fullHtml?: string;
}

function convertToLink(
  highlightedText: Element,
  excerpt: {
    context: string | undefined;
    matched_line: string | undefined;
    detail?: string | undefined;
    excerpt_link?: string | undefined;
    fullHtml?: string | undefined;
  }
) {
  const parent = highlightedText.parentElement;
  if (parent) {
    const aTag = document.createElement("a");
    aTag.href = excerpt?.excerpt_link || "";
    aTag.target = "_blank";
    aTag.referrerPolicy = "no-referrer";
    aTag.appendChild(highlightedText.cloneNode(true)); // Append clone of highlighted text to a tag
    parent.replaceChild(aTag, highlightedText); // Replace highlighted text with a tag
  }
}

export function scrollToExcerpt() {
  const lastHighlighted = document.querySelector(".highlighted-cited-excerpt") as HTMLElement;
  lastHighlighted?.scrollIntoView({
    behavior: "smooth",
    block: "center",
    inline: "nearest",
  });
}

interface CitedExcerptProps {
  citation: Citation;
  activeTab?: Tabs;
}

export const CitedExcerpt: React.FC<CitedExcerptProps> = ({ activeTab, citation }) => {
  const { citedExcerpt, isLoading } = useGetCitedExcerpt({ citation });
  const [isError, setIsError] = useState<boolean>(false);
  const excerptRef = useRef(null); // Ref for the container where fullHtml will be rendered
  const { document: citationDocument, loading: documentViewIdLoading } = useDocument({
    citation,
  });
  const content = citationDocument?.content;

  useEffect(() => {
    if (!isLoading && content && citedExcerpt?.matched_line) {
      highlightPlainText({
        text: citedExcerpt.matched_line,
        container: ".excerpt-container",
        highlightClassName: "highlighted-cited-excerpt",
        shouldScroll: false,
        backgroundColor: "#BFDBFE",
      }).then(() => {
        document.querySelectorAll(".highlighted-cited-excerpt").forEach((highlightedText) => {
          if (citedExcerpt) convertToLink(highlightedText, citedExcerpt);
        });
        scrollToExcerpt();
      });
    }
  }, [citedExcerpt, isLoading, content]);

  useEffect(() => {
    if (activeTab === Tabs.citedExcerpt && !isLoading && citedExcerpt?.matched_line && content) {
      scrollToExcerpt();
    }
  }, [activeTab, citedExcerpt, isLoading, content]);

  return isLoading ? (
    <TypingIndicator />
  ) : isError ? (
    <div>Error loading the cited excerpt</div>
  ) : (
    <div>
      <div
        className="excerpt-container text-[15px]"
        ref={excerptRef}
        dangerouslySetInnerHTML={{ __html: content || "" }}
      />
      <div>{citedExcerpt?.detail}</div>
    </div>
  );
};
